import { Controller } from '@hotwired/stimulus';

// The page's half of OPEN in Telegram (Jane::MiniAppController, docs/BOTS.md
// § The screen behind OPEN). Drawn by "jane/mini_app/bridge" on every page
// inside Telegram, and inert anywhere else: a browser has no initData, so the
// page is just a page and stays fully usable.
//
// Four jobs and no more: tell Telegram the page is ready and what colour it
// is, post the signed launch once so the session knows who opened it, turn
// the page's one `[data-tg-main]` into Telegram's own bottom button, and show
// Telegram's back arrow everywhere but the home page.
//
// ready() also runs inline in the bridge, before this bundle. It is here
// again for a later visit (Turbo does not re-run that script) and it is not
// behind initData: an empty launch still has to drop the splash.
// Registered as "mini-app" by registerJane (register.js).
export default class extends Controller {
  static values = { session: String, home: String };

  connect() {
    this.tg = window.Telegram?.WebApp;
    if (!this.tg) return;

    this.tg.ready();
    if (!this.tg.initData) return;

    this.tg.expand();
    // A list scrolls down; Telegram would read the same swipe as "close".
    this.tg.disableVerticalSwipes?.();
    this.tg.onEvent('themeChanged', this.paint);
    this.paint();
    this.back();
    this.main();
    this.arrive();
  }

  disconnect() {
    if (!this.tg?.initData) return;

    this.tg.offEvent('themeChanged', this.paint);
    this.tg.MainButton.offClick(this.press);
    this.tg.MainButton.hide();
    this.tg.BackButton.offClick(this.pop);
  }

  // Telegram's chrome in the page's own colour, and the page in Telegram's
  // scheme — when the host has a theme by that name (data-themes on <html>).
  paint = () => {
    const root = document.documentElement;
    const themes = JSON.parse(root.dataset.themes || '[]');
    if (themes.includes(this.tg.colorScheme)) root.dataset.theme = this.tg.colorScheme;

    const color = this.hex(getComputedStyle(document.body).backgroundColor);
    for (const set of ['setHeaderColor', 'setBackgroundColor', 'setBottomBarColor']) {
      try {
        this.tg[set]?.(color);
      } catch {
        // an older client: it keeps its own colours
      }
    }
  };

  back() {
    const back = this.tg.BackButton;
    if (location.pathname === this.homeValue) return back.hide();

    back.onClick(this.pop);
    back.show();
  }

  pop = () => (history.length > 1 ? history.back() : (location.href = this.homeValue));

  // The page's call to action, drawn by Telegram where a thumb expects it.
  // The element stays in the page (and is the whole button in a browser);
  // pressing Telegram's copy presses it.
  main() {
    const button = this.tg.MainButton;
    this.target = document.querySelector('[data-tg-main]');
    if (!this.target) return button.hide();

    const label = this.target.dataset.tgMain;
    button.setText(label && label !== 'true' ? label : this.target.textContent.trim());
    button.onClick(this.press);
    button.show();
    this.target.style.display = 'none';
  }

  press = () => this.target?.click();

  // Telegram's signed word on who opened this, once per webview — not once
  // per page. A changed session redraws in place. A full reload puts the
  // splash back up and it stays there. The flag goes down first so a webview
  // that refuses cookies cannot do it twice.
  async arrive() {
    const key = 'jane:tg-arrived';
    if (window.__janeTgArrived) return;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch {
      // storage refused: post anyway. A launch is good once; the session the
      // first post signed in answers the second (Jane::MiniAppController#create)
    }

    try {
      const res = await fetch(this.sessionValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.content,
          Accept: 'application/json',
        },
        body: new URLSearchParams({ init_data: this.tg.initData }),
      });
      if (!res.ok) return;

      window.__janeTgArrived = true;
      try {
        sessionStorage.setItem(key, '1');
      } catch {
        // see above
      }
      // A deep link's word lands on its page (Jane::MiniAppController#landing).
      const { changed, go } = await res.json();
      if (go) location.replace(go);
      else if (changed && window.Turbo) window.Turbo.visit(location.href, { action: 'replace' });
    } catch {
      // offline: the page is still the page
    }
  }

  // Any CSS colour (daisyUI draws in oklch) as the #rrggbb Telegram wants,
  // read back off a canvas pixel — a canvas will not hand oklch back as hex.
  hex(color) {
    const ctx = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 1, 1);
    return `#${[...ctx.getImageData(0, 0, 1, 1).data.slice(0, 3)].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
  }
}
